import React from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { ChevronLeft, IconButton, Typography } from "@coolremote/shared";
import styles from "./DeviceRegistrationPublic.module.css";

interface FlowStepPlaceholderProps {
  title: string;
  backTo: string;
}

/**
 * Generic "this step isn't built yet" screen for the public registration flow.
 * Used for routes a later subtask fills in, so the subtask that creates the
 * route can still be tested and merged on its own — see CM-617's
 * `/register-device/new-user` and `/register-device/existing-user`.
 */
const FlowStepPlaceholder: React.FC<FlowStepPlaceholderProps> = ({ title, backTo }) => {
  const history = useHistory();

  const handleBack = () => {
    // Real browser back, not a fresh push — a push with no state would fail
    // UserSelection's guard (no validated device in location.state) and bounce
    // all the way back to /register-device instead of landing on user-selection.
    // Falls back to a plain push only when there is no in-app history to pop
    // (e.g. this route was hit directly by URL).
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push(backTo);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.appBar}>
        <IconButton
          className={styles.backButton}
          onClick={handleBack}
          aria-label={t`Back`}
        >
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={styles.appBarTitle}>{title}</span>
        <span />
      </div>

      <div className={styles.content}>
        <Typography variant="sectionTitle">{title}</Typography>
        <Typography variant="meta">
          {t`This step is being built. Coming soon.`}
        </Typography>
      </div>
    </div>
  );
};

export default FlowStepPlaceholder;
